import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { Reveal } from "./Reveal";
import { clinics } from "./Network";
import { Loader2 } from "lucide-react";
import { trackGA } from "@/lib/consent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const initial = { name: "", phone: "", email: "", city: "", message: "", privacy_consent: false, website: "" };

export const ContactSection = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy_consent) {
      toast.error("È necessario accettare l'informativa privacy.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, email: form.email || null, city: form.city || null, message: form.message || null, source: "contatti" });
      if (window.fbq) window.fbq("track", "Lead");
      trackGA("generate_lead", { form: "contatti" });
      setForm(initial);
      navigate("/grazie");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Si è verificato un errore. Riprova o scrivici su WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-[#D8DDD9] rounded-xl px-5 py-4 text-base text-[#15201A] placeholder:text-[#9AA79E] focus:outline-none focus:border-[#385244] focus:ring-2 focus:ring-[#385244]/20 transition-colors duration-300";

  return (
    <section id="contatti" className="py-24 md:py-32 px-6 md:px-12 bg-[#DDEBF2]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14">
        <Reveal>
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#7D9285] mb-4">Contatti</p>
          <h2 className="font-serif-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#15201A]">
            Richiedi una <em className="italic text-[#385244]">valutazione</em> per i tuoi impianti dentali
          </h2>
          <p className="mt-6 text-base md:text-lg leading-relaxed text-[#5C6B61] max-w-lg">
            Compila il modulo: un nostro incaricato ti richiamerà entro 24 ore lavorative per fissare
            la prima visita nello studio più vicino a te.
          </p>
          <ul className="mt-10 space-y-3 text-[#385244] font-semibold">
            <li>✓ Prima visita e TAC in sede</li>
            <li>✓ Preventivo scritto e definitivo</li>
            <li>✓ Nessun obbligo di cura</li>
          </ul>
        </Reveal>
        <Reveal delay={0.15}>
          <form onSubmit={submit} className="bg-white rounded-[32px] border border-border p-8 md:p-10 space-y-5" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <input required value={form.name} onChange={set("name")} placeholder="Nome e cognome *" className={inputCls} data-testid="contact-name-input" />
              <input required value={form.phone} onChange={set("phone")} placeholder="Telefono *" type="tel" className={inputCls} data-testid="contact-phone-input" />
            </div>
            <input value={form.email} onChange={set("email")} placeholder="Email (per ricevere la copia digitale del libro in omaggio)" type="email" className={inputCls} data-testid="contact-email-input" />
            <input value={form.website} onChange={set("website")} name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
            <select value={form.city} onChange={set("city")} className={inputCls} data-testid="contact-city-select">
              <option value="">Studio preferito (facoltativo)</option>
              {clinics.map((c) => (
                <option key={c.city} value={`${c.city} (${c.prov})`}>{`${c.city} (${c.prov})`}</option>
              ))}
            </select>
            <textarea value={form.message} onChange={set("message")} placeholder="Raccontaci la tua situazione (facoltativo)" rows={4} className={inputCls} data-testid="contact-message-input" />
            <label className="flex items-start gap-3 text-sm text-[#5C6B61] cursor-pointer">
              <input
                type="checkbox"
                checked={form.privacy_consent}
                onChange={(e) => setForm({ ...form, privacy_consent: e.target.checked })}
                className="mt-1 w-5 h-5 accent-[#385244]"
                data-testid="contact-privacy-checkbox"
              />
              <span>
                Ho letto l'<Link to="/privacy-policy" className="underline text-[#385244]">informativa privacy</Link> e acconsento
                al trattamento dei miei dati per essere ricontattato. *
              </span>
            </label>
            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex justify-center items-center gap-2 rounded-full bg-[#C98E6E] hover:bg-[#B37A5B] disabled:opacity-60 text-white text-lg font-bold px-8 py-4 transition-colors duration-300 active:scale-[0.98]"
              data-testid="contact-submit-btn"
            >
              {loading && <Loader2 className="animate-spin" size={20} />}
              Invia la richiesta
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
